import React from 'react'
import find from 'lodash/find'
import truncate from 'lodash/truncate'
import map from 'lodash/map'
import range from 'lodash/range'
import { observer } from 'mobx-react'
import ShadowBox from 'components/ShadowBox'
import Avatar from 'components/Avatar'
import Icon from 'components/Icon'
import RadialProgress from 'components/RadialProgress'
import { FlexContainer, FlexItem } from 'components/Flex'
import { Form, Input, Select } from 'components/Form'
import FileTypeIcon from 'components/FileTypeIcon'
import './styles.sass'

const Document = ({ document, canChangeCategory }) => (
  <ShadowBox className="document" level={2}>
    {document.editor.editing
      ? <DocumentEditMode document={document} canChangeCategory={canChangeCategory} />
      : <DocumentDisplayMode document={document} onDownloadClick={() => document.download()} />
    }
  </ShadowBox>
)

export default observer(Document)

let DocumentDisplayMode = ({ document, onDownloadClick }) => (
  <div className="document-display-mode">
    <FlexContainer>
      <FlexItem stretch>
        <FlexContainer>
          <FlexItem stretch>
            <h3 className="title is-5 is-semibold is-marginless">
              {truncate(document.label || document.fileName, 20)}
              {document.friendlyType &&
                <span className="category">
                  &nbsp;
                  ({document.friendlyType})
                </span>
              }
            </h3>
          </FlexItem>
          {!document.isNew &&
            <FlexItem className="top-actions">
              <button className="button is-icon-only is-faded" onClick={e => document.editor.startEditing()}>
                <Icon material name="edit" small />
              </button>
            </FlexItem>
          }
        </FlexContainer>
        <FlexContainer className="document-meta-container">
          {document.uploadedBy &&
            <FlexItem>
              <Avatar size={20} url={document.uploadedBy.avatar} fallback={document.uploadedBy.avatarFallback} />
            </FlexItem>
          }
          <FlexItem stretch className="document-meta">
            {document.uploadedBy &&
              <span className="uploader">
                {document.uploadedBy.name}
                &nbsp;
              </span>
            }
            <span className="date">
              {document.dateUpdated.format('ll')}
            </span>
          </FlexItem>
        </FlexContainer>

        {document.description &&
          <p className="document-description">
            {document.description}
          </p>
        }

        {!document.isNew &&
          <div className="actions">
            <a
              className="unstyled download-button"
              onClick={onDownloadClick}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FlexContainer>
                <FlexItem>
                  <FileTypeIcon
                    height="48"
                    primaryColor={_iconType(document.fileType).color}
                    strokeColor="#ccc"
                    type={_iconType(document.fileType).type}
                    label={document.fileType}
                    animate
                  />
                </FlexItem>
                <FlexItem stretch className="file-meta">
                  <h5 className="file-name">
                    {document.fileName}
                  </h5>
                  <div className="file-description">
                    Download
                  </div>
                </FlexItem>
              </FlexContainer>
            </a>
          </div>
        }
      </FlexItem>
      {document.isNew &&
        <FlexItem>
          <RadialProgress
            size={50}
            progress={document.uploadProgress || 0}
          />
        </FlexItem>
      }
    </FlexContainer>

  </div>
)

DocumentDisplayMode = observer(DocumentDisplayMode)

let DocumentEditMode = ({ document, canChangeCategory }) => (
  <div className="document-edit-mode">
    <Form onSubmit={() => document.editor.save()}>
      <div className="editor-area">
        <div className="columns">
          <div className="column">
            <Input
              label="File name"
              model={document.editor}
              disabled={document.editor.disabled}
              field="label"
            />
          </div>
        </div>
        <div className="columns">
          <div className="column">
            <Input
              label="Notes"
              model={document.editor}
              disabled={document.editor.disabled}
              field="description"
            />
          </div>
        </div>
        {canChangeCategory &&
          <div className="columns">
            <div className="column">
              <Select
                label="Category"
                model={document.editor}
                disabled={document.editor.disabled}
                field="category"
              >
                <option value="">Select...</option>
                <option value="PERSONAL">Personal</option>
                <option value="BUSINESS">Business</option>
              </Select>
            </div>
          </div>
        }
        <div className="columns">
          <div className="column">
            <Select
              label="Document type"
              model={document.editor}
              disabled={document.editor.disabled}
              field="type"
            >
              <option value="">Select...</option>
              {map(document.editor.availableDocumentTypes, (text, type) =>
                <option key={type} value={type}>{text}</option>
              )}
            </Select>
          </div>
          <div className="column">
            <Select
              label="Year"
              model={document.editor}
              disabled={document.editor.disabled}
              field="year"
            >
              <option value="">Select year...</option>
              {_years.map((year) =>
                <option key={year} value={year}>{year}</option>
              )}
            </Select>
          </div>
        </div>
      </div>
      <div className="editor-actions">
        <button
          type="submit"
          className="button is-save"
          disabled={document.editor.disabled}
        >
          {document.isNew ? 'Upload' : 'Save'}
        </button>
        <button
          type="button"
          className="button is-cancel"
          disabled={document.editor.disabled}
          onClick={() => document.editor.cancelEditing()}
        >
          Cancel
        </button>
      </div>
   </Form>
  </div>
)

DocumentEditMode = observer(DocumentEditMode)

const _years = range(0, 6).map((i) => (new Date().getFullYear() - i).toString())
const _fileTypes = [
  {
    expr: /(xlsx?|numbers)/i,
    color: '#70c79a',
    type: 'table'
  },
  {
    expr: /(png|jpe?g|tiff|gif)/i,
    color: '#40a0f4',
    type: 'image'
  },
  {
    expr: /(js|cs|java|md|c|py|ts)/i,
    color: '#999',
    type: 'code'
  },
  {
    expr: /(pptx?|key)/i,
    color: '#FF5252',
    type: 'slides'
  },
  {
    expr: /.*/,
    color: '#FF5252',
    type: 'text'
  }
]

const _iconType = (fileType) => find(_fileTypes, (opts) => fileType.match(opts.expr))



// WEBPACK FOOTER //
// ./src/client/components/Document/index.jsx